import { FC, useEffect } from 'react';
import { Modal, Stack, Alert, Button, Loader, Text } from '@mantine/core';
import { IconAlertCircle, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useDocumentScanner } from './hooks/useDocumentScanner';
import { CameraView } from './CameraView';
import { ScanPreview } from './ScanPreview';
import { ScanSummary } from './ScanSummary';
import { isCameraSupported } from './utils/scannerUtils';

export interface DocumentScannerModalProps {
  opened: boolean;
  onClose: () => void;
  onScanComplete: (file: File) => void;
  preferredCamera?: 'user' | 'environment';
}

export const DocumentScannerModal: FC<DocumentScannerModalProps> = ({
  opened,
  onClose,
  onScanComplete,
  preferredCamera = 'environment',
}) => {
  const { t } = useTranslation(['common']);

  const {
    state,
    error,
    stream,
    processedImage,
    scannedPages,
    documentMode,
    videoRef,
    canvasRef,
    initializeScanner,
    captureImage,
    highlightDocument,
    setDocumentMode,
    goToSummary,
    addMorePages,
    removePageFromBatch,
    finalizeBatchScan,
    retake,
    reset,
  } = useDocumentScanner({
    preferredCamera,
    onScanComplete: (file) => {
      onScanComplete(file);
      handleClose();
    },
    onError: (err) => {
      console.error('Scanner error:', err);
    },
  });

  // Initialize scanner when modal opens
  useEffect(() => {
    if (opened && state === 'idle') {
      initializeScanner();
    }
  }, [opened, state, initializeScanner]);

  const handleClose = () => {
    reset();
    onClose();
  };

  // Check if camera is supported
  if (!isCameraSupported()) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title={t('documentScanner.title', {
          defaultValue: 'Scan Document',
        })}
        size="lg"
      >
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {t('documentScanner.cameraNotSupported', {
            defaultValue:
              'Camera is not supported in this browser. Please use a modern browser with camera support.',
          })}
        </Alert>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('documentScanner.title', {
        defaultValue: 'Scan Document',
      })}
      size="xl"
      closeOnClickOutside={state !== 'processing'}
      closeOnEscape={state !== 'processing'}
    >
      <Stack gap="md">
        {/* Error Alert */}
        {error && state === 'error' && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => initializeScanner()}
          >
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                {error}
              </Text>
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={initializeScanner}
              >
                {t('documentScanner.retry', {
                  defaultValue: 'Retry',
                })}
              </Button>
            </Stack>
          </Alert>
        )}

        {/* Initializing State */}
        {state === 'initializing' && (
          <Stack align="center" gap="md" py="xl">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              {t('documentScanner.initializing', {
                defaultValue: 'Initializing scanner...',
              })}
            </Text>
          </Stack>
        )}

        {/* Camera View */}
        {state === 'camera' && (
          <CameraView
            stream={stream}
            videoRef={videoRef}
            canvasRef={canvasRef}
            onCapture={captureImage}
            onHighlight={highlightDocument}
            documentMode={documentMode}
            onDocumentModeChange={setDocumentMode}
          />
        )}

        {/* Processing State */}
        {state === 'processing' && (
          <Stack align="center" gap="md" py="xl">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              {t('documentScanner.processing', {
                defaultValue: 'Processing document...',
              })}
            </Text>
          </Stack>
        )}

        {/* Preview State - simplified */}
        {state === 'preview' && (
          <ScanPreview
            processedImage={processedImage}
            onKeep={goToSummary}
            onRetake={retake}
            isProcessing={false}
          />
        )}

        {/* Summary State - review all pages before save */}
        {state === 'summary' && (
          <ScanSummary
            pages={scannedPages}
            onRemovePage={removePageFromBatch}
            onAddMorePages={addMorePages}
            onFinalize={finalizeBatchScan}
            isProcessing={false}
          />
        )}

        {/* Cancel button (always visible except when processing) */}
        {state !== 'processing' && state !== 'initializing' && (
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconX size={16} />}
            onClick={handleClose}
            fullWidth
          >
            {t('cancel', {
              defaultValue: 'Cancel',
            })}
          </Button>
        )}
      </Stack>
    </Modal>
  );
};

export default DocumentScannerModal;
